import { useState } from 'react';
import { contactInfo } from '../data/siteData';
import Reveal from './Reveal';
import './Contact.css';

const headOffice = contactInfo.find((item) => item.label === 'Head Office');
const email = contactInfo.find((item) => item.label === 'Email');
const hours = contactInfo.find((item) => item.label === 'Hours');

// Sign up free at formspree.io, create a form pointed at enquiry@sundaravelgroup.com,
// and replace this with the endpoint it gives you (looks like 'https://formspree.io/f/xxxxabcd').
const FORMSPREE_ENDPOINT = '';

function EnquiryForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!FORMSPREE_ENDPOINT) {
      // Not configured yet — fall back to the visitor's own email client so the
      // form still does something useful in the meantime, and still show a
      // confirmation so submitting doesn't look like it did nothing.
      const to = email ? email.value : '';
      const subject = `Enquiry from ${form.name || 'Website Visitor'}`;
      const body = `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\n\n${form.message}`;
      window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
        body
      )}`;
      setStatus('sent');
      setForm({ name: '', email: '', phone: '', message: '' });
      return;
    }

    setStatus('sending');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(e.target),
      });

      if (res.ok) {
        setStatus('sent');
        setForm({ name: '', email: '', phone: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className="enquiry-success">
        <span className="enquiry-success-icon" aria-hidden="true">
          &#10003;
        </span>
        <p>
          {FORMSPREE_ENDPOINT
            ? "Thanks — your enquiry has been sent. We'll get back to you soon."
            : 'Your email app should now open with the enquiry pre-filled — just hit send there to reach us.'}
        </p>
        <button type="button" className="btn btn-outline" onClick={() => setStatus('idle')}>
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form className="enquiry-form" onSubmit={handleSubmit}>
      <div className="enquiry-row">
        <label className="enquiry-field">
          <span className="enquiry-label">Name</span>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Your full name"
            required
          />
        </label>

        <label className="enquiry-field">
          <span className="enquiry-label">Phone</span>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="Your phone number"
          />
        </label>
      </div>

      <label className="enquiry-field">
        <span className="enquiry-label">Email</span>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder="you@example.com"
          required
        />
      </label>

      <label className="enquiry-field">
        <span className="enquiry-label">Message</span>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          placeholder="Tell us how we can help"
          rows={4}
          required
        />
      </label>

      <button type="submit" className="btn btn-primary enquiry-submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Send Enquiry'}
      </button>

      {status === 'error' && (
        <p className="enquiry-error">
          Something went wrong sending your enquiry. Please try again, or email us directly at{' '}
          {email && <a href={`mailto:${email.value}`}>{email.value}</a>}.
        </p>
      )}
    </form>
  );
}

function Contact() {
  return (
    <section id="contact" className="section contact">
      <div className="section-inner">
        <Reveal variant="up" className="contact-heading-block">
          <span className="eyebrow" style={{ justifyContent: 'center' }}>
            Get In Touch
          </span>
          <h2 className="contact-heading">
            Let's <span className="accent">Connect</span>
          </h2>
          <p className="contact-intro">
            Whether you're a business partner, supplier, prospective employee, or student —
            we'd love to hear from you.
          </p>
        </Reveal>

        <div className="contact-layout">
          {headOffice && (
            <Reveal variant="scale" as="div" className="contact-card contact-office-card">
              <div className="contact-label">Head Office</div>
              <div className="contact-value">{headOffice.value}</div>

              <div className="contact-office-map">
                <iframe
                  title="Sundaravel Group — Head Office location"
                  src={`https://maps.google.com/maps?q=${headOffice.lat},${headOffice.lng}&z=16&output=embed`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          )}

          <Reveal
            variant="scale"
            delay={150}
            as="div"
            className="contact-card contact-reach-card contact-form-card"
          >
            <div className="contact-label">Enquiry Form</div>
            <EnquiryForm />

            <div className="contact-form-meta">
              {email && (
                <a className="contact-form-meta-item" href={`mailto:${email.value}`}>
                  {email.value}
                </a>
              )}
              {hours && <span className="contact-form-meta-item">{hours.value}</span>}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default Contact;
